import type { TOC } from '@ember/component/template-only';
import {
  PrismButton,
  PrismCard,
  PrismIcon,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t, tKey } from 'ember-intl';
import { eq, or } from 'ember-truth-helpers';
import { Illustration } from 'frontend-utils/index';
import CalendarPlaceholder from 'lumen/assets/images/market-insight/empty-state/calendar-placeholder.svg?raw';
import MarketGraphPlaceholder from 'lumen/assets/images/market-insight/empty-state/market-graph-placeholder.svg?raw';
import UpsellChart from 'lumen/assets/images/market-insight/upsell/upsell-chart.svg?raw';
import GeneralPageMain from 'lumen/components/general/page/main';
import type HotelMarketInsightUpsellController from 'lumen/controllers/hotel/market-insight-upsell';

import styles from './market-insight-upsell.module.css';

interface HotelMarketInsightUpsellSignature {
  Args: {
    controller: HotelMarketInsightUpsellController;
    model: unknown;
  };
}

const BENEFITS = [
  tKey('upsell.market_insight.banner.benefit.1'),
  tKey('upsell.market_insight.banner.benefit.2'),
  tKey('upsell.market_insight.banner.benefit.3'),
];

<template>
  <GeneralPageMain class="navbar-settings">
    <div class="main-sidebar-and-content-container flex">
      <div>
        <Illustration @html={{CalendarPlaceholder}} class="h-full" />
      </div>
      <PrismCard class="relative m-500 max-h-[560px] flex-1 overflow-hidden">
        <:content>
          <Illustration @html={{MarketGraphPlaceholder}} class="h-full" />

          <div class={{styles.overlay}} />

          {{#if
            (eq
              @controller.activeModalView
              @controller.MODAL_VIEWS.AWAITING_TRIAL_SETUP
            )
          }}
            <div class={{styles.overlay-message}}>
              <PrismMessage
                @description={{t
                  "upsell.market_insight.awaiting_trial.description"
                }}
                @illustration="100x100_progress"
                @title={{t "upsell.market_insight.awaiting_trial.title"}}
              />
            </div>
          {{/if}}
        </:content>

      </PrismCard>
    </div>

    {{#if
      (or
        (eq @controller.activeModalView @controller.MODAL_VIEWS.REQUEST_TRIAL)
        (eq @controller.activeModalView @controller.MODAL_VIEWS.TRIAL_ENDED)
      )
    }}
      <div class={{styles.banner}}>
        <div class={{styles.info}}>
          <h1 class="text-neutral-emphasis w-[300px]">
            {{t "upsell.market_insight.title"}}
          </h1>

          <div class="w-[300px]">
            <ul class="paragraph-300--regular flex flex-col gap-400">
              {{#each BENEFITS as |benefit|}}
                <li class="flex gap-200">
                  <PrismIcon @name="checkmark" @size="small" class="mt-100" />
                  <span>
                    {{t benefit}}
                  </span>
                </li>
              {{/each}}
            </ul>

            <PrismButton
              @buttonType="brand"
              @iconAfter="arrow-right"
              @isDisabled={{or
                @controller.isLoading
                @controller.access.isStaffUser
              }}
              @label={{t "upsell.market_insight.start_trial"}}
              @onClick={{@controller.requestTrial}}
              class="mt-400 w-full"
            />

            <a
              class="prism-button prism-button--secondary mt-200 w-full"
              href="https://www.mylighthouse.com/market-intelligence/market-insight"
              rel="noopener noreferrer"
              target="_blank"
            >
              {{t "general.learn_more"}}
            </a>
          </div>
        </div>

        <div class={{styles.image}}>
          <Illustration @html={{UpsellChart}} class="w-full" />
        </div>
      </div>
    {{/if}}
  </GeneralPageMain>
</template> satisfies TOC<HotelMarketInsightUpsellSignature>;
